import {
  FormControlLabel,
  Grid,
  MenuItem,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { endOfDay, isBefore } from "date-fns";
import { useEffect } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import InfoTooltip from "../../../../../../common/components/functional/InfoTooltip/InfoTooltip";
import { SelectLight } from "../../../../../../common/components/styled/styled";
import { HOLIDAY_ACCUMULATION_RULE_OPTIONS } from "../../../../types/annualHolidayAccumulationRule";
import { WorkPeriodType } from "../../../../types/workPeriodType";
import { WorkTimeType } from "../../../../types/workTimeType";
import { FormKeys, WorkOfferType } from "../helpers";
import { FormSectionComponent } from "./FormSectionComponent";
import { PeriodWorkHours } from "./components/PeriodWorkHours";
import { ProbationaryPeriod } from "./components/ProbationaryPeriod";
import { StartEndDates } from "./components/StartEndDates";
import {
  WORK_AGREEMENT_ACCEPTED,
  WORK_AGREEMENT_DECLINED,
} from "../../../../constants";
import { SalaryType } from "../../../../types/salaryType";

type Props = {
  periodStartDateIsRequired: boolean | null;
  type: WorkOfferType;
};

export const WorkingTime = ({ periodStartDateIsRequired, type }: Props) => {
  const { t } = useTranslation();
  const {
    getValues,
    control,
    watch,
    formState: { errors },
    setValue,
  } = useFormContext();

  const watchedWorkPeriodType = watch(FormKeys.PeriodType);
  const watchedSalaryType = watch(FormKeys.SalaryType);
  const salaryIsMonthly = watchedSalaryType === SalaryType.Monthly;

  const validatePeriodStartDate = (date: Date) => {
    const endOfWorkStartsDay = endOfDay(getValues(FormKeys.WorkStarts));
    if (date === null) return true;
    if (isBefore(date, endOfWorkStartsDay)) return true;

    return "invalidDate";
  };

  const status = getValues("status");
  const isReadOnly: boolean =
    status === WORK_AGREEMENT_ACCEPTED ||
    status === WORK_AGREEMENT_DECLINED ||
    type !== WorkOfferType.New;

  useEffect(() => {
    if (!periodStartDateIsRequired) {
      setValue(FormKeys.PeriodStartDate, null);
    }
  }, [periodStartDateIsRequired, setValue]);

  const rows = [
    {
      title: t("order:startAndEndDate"),
      component: <StartEndDates isReadOnly={isReadOnly} />,
    },
    {
      title: t("order:probationaryPeriodEnds"),
      component: (
        <ProbationaryPeriod
          isReadOnly={isReadOnly}
          isPeriodDisabledWhenNoDate={type !== WorkOfferType.New}
        />
      ),
    },
    {
      title: t("order:workingHours"),
      component: (
        <Controller
          control={control}
          name={FormKeys.WorkTimeType}
          render={({ field: { value, onChange } }) => (
            <RadioGroup
              name={FormKeys.WorkTimeType}
              value={value}
              onChange={(event) => {
                if (event.target.value === WorkTimeType.Variable) {
                  setValue(FormKeys.SalaryType, SalaryType.Hourly);
                }
                onChange(event.target.value);
              }}
            >
              <FormControlLabel
                value={WorkTimeType.FullTime}
                control={<Radio />}
                label={t("order:fullTime")}
                disabled={isReadOnly}
              />
              <FormControlLabel
                value={WorkTimeType.PartTime}
                control={<Radio />}
                label={t("order:partTime")}
                disabled={isReadOnly}
              />
              <FormControlLabel
                value={WorkTimeType.Variable}
                control={<Radio />}
                label={t("order:variable")}
                disabled={isReadOnly || salaryIsMonthly}
              />
            </RadioGroup>
          )}
        />
      ),
    },
    {
      title:
        watchedWorkPeriodType === WorkPeriodType.Weekly
          ? t("talentProfile:weeklyWorkHours")
          : t("talentProfile:periodWorkHours"),
      component: <PeriodWorkHours isReadOnly={isReadOnly} />,
    },
    {
      title: t("talentProfile:periodStarts"),
      component: periodStartDateIsRequired ? (
        <Controller
          control={control}
          name={FormKeys.PeriodStartDate}
          rules={{
            // TODO required validation doesn't work properly.
            // - If we value changes between true / false, red borders stay for some reason - is this still valid?
            required: watch(FormKeys.PeriodStartDateIsRequired),
            validate: (date) => validatePeriodStartDate(date),
          }}
          render={({ field, fieldState }) => (
            <DatePicker
              {...field}
              disabled={isReadOnly}
              slotProps={{
                textField: {
                  size: "small",
                  label: null,
                  error: !!fieldState.error || !!errors[field.name],
                },
              }}
              sx={{
                width: "150px",
              }}
            />
          )}
        />
      ) : (
        <>-</>
      ),
    },
    {
      title: t("talentProfile:isAveragingPeriodInUse"),
      component: (
        <Typography>
          {watch(FormKeys.IsAveragingPeriodInUse)
            ? t("common:inUse")
            : t("common:notInUse")}
        </Typography>
      ),
    },
    {
      title: t("talentProfile:agreeToOvertime"),
      component: (
        <Controller
          control={control}
          name={FormKeys.TalentAgreesExtraWork}
          render={({ field: { value, onChange } }) => (
            <RadioGroup
              name={FormKeys.TalentAgreesExtraWork}
              value={value}
              onChange={(_, value) => {
                const booleanValue = value === "true" ? true : false;
                onChange(booleanValue);
              }}
            >
              <FormControlLabel
                value={true}
                control={<Radio />}
                label={t("common:yes")}
                disabled={isReadOnly}
              />
              <FormControlLabel
                value={false}
                control={<Radio />}
                label={t("common:no")}
                disabled={isReadOnly}
              />
            </RadioGroup>
          )}
        />
      ),
    },
    {
      title: t("order:holidayAccumulationRule"),
      component: (
        <Controller
          control={control}
          name={FormKeys.HolidayAccumulationRule}
          rules={{ required: true }}
          render={({ field: { value, onChange } }) => (
            <>
              <SelectLight
                name={FormKeys.HolidayAccumulationRule}
                value={value}
                disabled={isReadOnly}
                onChange={onChange}
                size="small"
                sx={{ width: "155px" }}
                error={!!errors[FormKeys.HolidayAccumulationRule]}
              >
                {HOLIDAY_ACCUMULATION_RULE_OPTIONS.map((option) => (
                  <MenuItem key={option} value={option}>
                    {t(`order:${option}`)}
                  </MenuItem>
                ))}
              </SelectLight>
              <Grid item marginLeft="18px" alignSelf="center">
                <InfoTooltip
                  title={t("order:holidayAccumulationRuleTooltip")}
                />
              </Grid>
            </>
          )}
        />
      ),
    },
  ];

  return <FormSectionComponent title={t("order:workingHours")} rows={rows} />;
};
